import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, PlanType } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

const CHECKOUT_URL = 'https://obkzsvawsvwqrfsssuak.supabase.co/functions/v1/create-checkout-session';

const plans = [
  {
    id: 'premium' as PlanType,
    name: 'Premium',
    price: '2,50€',
    period: '/mes',
    accent: '#00e87a',
    features: [
      'Estadísticas y tendencias avanzadas',
      'Alertas Dropping Odds (20/día)',
      'Gestor de bankroll personal',
      'Filtros cruzados sin límite',
      'Comparador completo (6 casas)',
      'Unirse a ligas competitivas',
      'Publicar picks en el foro',
    ],
    highlight: true,
  },
  {
    id: 'elite' as PlanType,
    name: 'Elite',
    price: '5,00€',
    period: '/mes',
    accent: '#ffd700',
    features: [
      'Todo lo de Premium',
      'Alertas SMS ilimitadas',
      'Ligas privadas ilimitadas',
      'Badge Elite verificado',
      'Datos históricos (5 años)',
      'Soporte prioritario 24/7',
    ],
    highlight: false,
  },
];

export default function PaywallModal() {
  const { paywallModalOpen, closePaywallModal, paywallTargetPlan, isRegistered, isAdmin, openRegisterModal, supabaseUser } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState<PlanType>(null);
  const [error, setError] = useState('');

  // If targetPlan provided and user is logged in, auto-start checkout
  useEffect(() => {
    if (paywallModalOpen && paywallTargetPlan && isRegistered && supabaseUser) {
      handleSelectPlan(paywallTargetPlan);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paywallModalOpen, paywallTargetPlan]);

  // Admins bypass paywall entirely
  useEffect(() => {
    if (paywallModalOpen && isAdmin) {
      closePaywallModal();
    }
  }, [paywallModalOpen, isAdmin, closePaywallModal]);

  if (!paywallModalOpen || isAdmin) return null;

  const handleSelectPlan = async (planId: PlanType) => {
    if (!isRegistered || !supabaseUser) {
      closePaywallModal();
      openRegisterModal();
      return;
    }

    if (!planId) return;
    setError('');
    setLoadingPlan(planId);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const successUrl = `${window.location.origin}${window.location.pathname}`;
      const cancelUrl = `${window.location.origin}${window.location.pathname}`;

      const res = await fetch(CHECKOUT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          plan: planId,
          userId: supabaseUser.id,
          successUrl,
          cancelUrl,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Error al crear sesión de pago');
      }

      window.open(data.url, '_blank', 'noopener,noreferrer');
      closePaywallModal();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error inesperado';
      setError(message);
      setLoadingPlan(null);
    }
  };

  // If auto-checkout is in progress, show a full-screen loader
  const isAutoCheckout = !!(paywallTargetPlan && isRegistered);

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={closePaywallModal}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-2xl bg-[#0d0f14] border border-[#2a2d3a] rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#2a2d3a] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]">
                <i className="ri-vip-crown-line text-lg"></i>
              </div>
              <span className="text-[#ffd700] text-xs font-bold uppercase tracking-widest">
                {isAutoCheckout ? `Activando plan ${paywallTargetPlan === 'elite' ? 'Elite' : 'Premium'}` : 'Función Premium'}
              </span>
            </div>
            <h2 className="text-white font-black text-xl">
              {isAutoCheckout ? 'Redirigiendo a pago seguro...' : 'Desbloquea esta función'}
            </h2>
            <p className="text-[#6b7280] text-sm mt-1">
              {isAutoCheckout
                ? 'Un momento, abrimos Stripe en una nueva pestaña.'
                : 'Elige tu plan y completa el pago de forma segura con Stripe en nueva pestaña.'}
            </p>
          </div>
          <button
            onClick={closePaywallModal}
            className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Auto-checkout loading state */}
        {isAutoCheckout && loadingPlan ? (
          <div className="p-12 flex flex-col items-center gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#00e87a]/10">
              <i className="ri-loader-4-line text-[#00e87a] text-3xl animate-spin"></i>
            </div>
            <p className="text-white font-bold">Preparando tu pago con Stripe...</p>
            <p className="text-[#6b7280] text-sm">No cierres esta ventana</p>
          </div>
        ) : (
          <>
            {/* Plans */}
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative rounded-xl p-5 border transition-all ${
                    plan.id === paywallTargetPlan
                      ? plan.highlight
                        ? 'border-[#00e87a] bg-[#00e87a]/8 ring-1 ring-[#00e87a]/30'
                        : 'border-[#ffd700] bg-[#ffd700]/8 ring-1 ring-[#ffd700]/30'
                      : plan.highlight
                      ? 'border-[#00e87a] bg-[#00e87a]/5'
                      : 'border-[#2a2d3a] bg-[#151719] hover:border-white/20'
                  }`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-4 px-3 py-0.5 bg-[#00e87a] rounded-full">
                      <span className="text-black text-xs font-black uppercase">Más popular</span>
                    </div>
                  )}

                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: plan.accent }}>
                      {plan.name}
                    </span>
                    <div className="flex items-end gap-1 mt-1">
                      <span className="text-white text-3xl font-black">{plan.price}</span>
                      <span className="text-[#6b7280] text-sm mb-1">{plan.period}</span>
                    </div>
                    <p className="text-[#4a5568] text-xs mt-0.5">Suscripción mensual — cancela cuando quieras</p>
                  </div>

                  <ul className="space-y-2 mb-5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-[#a8b2c1]">
                        <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 flex-shrink-0" style={{ color: plan.accent }}>
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Coming soon — payment disabled */}
                  <div
                    className="w-full py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide whitespace-nowrap flex items-center justify-center gap-2 border border-dashed border-[#2a2d3a] text-[#6b7280] cursor-default"
                  >
                    <i className="ri-time-line text-sm"></i>
                    Próximamente disponible
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <div className="mx-6 mb-4 px-4 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-xs text-center">{error}</p>
              </div>
            )}

            <div className="px-6 pb-5 text-center">
              <p className="text-[#4a5568] text-xs">
                <i className="ri-time-line mr-1 text-[#ffd700]"></i>
                <span className="text-[#ffd700] font-bold">Los planes de pago estarán disponibles próximamente</span>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
